import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  FormControlLabel,
  Switch,
  IconButton,
  Autocomplete,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useProducts, Product } from '../context/ProductsContext';
import { useAuth } from '../context/AuthContext';
import {
  fetchSizeStockForProducts,
  saveSizeStockForProduct,
  SizeStockMap,
  fetchMiniOrders,
  MiniOrderRecord,
} from '../extraApi';
import { getColorLabel } from '../utils/colors';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
const ADMIN_PIN = '394500';
const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || 'bratanfamily59@gmail.com';
const MAX_COMPRESSED_BYTES = 800 * 1024; // ~800 Ko ciblés pour éviter la limite backend

const compressImageDataUrl = (
  originalDataUrl: string,
  initialQuality: number = 0.8,
  maxBytes: number = MAX_COMPRESSED_BYTES
): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();

    const computeSize = (dataUrl: string): number => {
      const parts = dataUrl.split(',');
      if (parts.length < 2) return dataUrl.length;
      const base64 = parts[1];
      return Math.floor((base64.length * 3) / 4);
    };

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(originalDataUrl);
        return;
      }

      let currentQuality = initialQuality;
      let currentDataUrl = originalDataUrl;

      const tryCompress = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        currentDataUrl = canvas.toDataURL('image/jpeg', currentQuality);

        const size = computeSize(currentDataUrl);
        if (size <= maxBytes || currentQuality <= 0.3) {
          resolve(currentDataUrl);
          return;
        }

        currentQuality -= 0.1;
        if (currentQuality < 0.3) {
          currentQuality = 0.3;
        }
        tryCompress();
      };

      // Si déjà en dessous du seuil, pas besoin de recomprimer
      if (computeSize(originalDataUrl) <= maxBytes) {
        resolve(originalDataUrl);
        return;
      }

      tryCompress();
    };

    img.onerror = () => {
      resolve(originalDataUrl);
    };

    img.src = originalDataUrl;
  });
};

type AdminOrderStatus = 'new' | 'in_progress' | 'completed';

interface AdminOrder {
  id: number;
  created_at: string;
  total_amount: number | string;
  status: AdminOrderStatus | string;
  user_email: string;
  user_name?: string | null;
  review_email_sent?: boolean | null;
  shipping_name?: string | null;
  shipping_address_line1?: string | null;
  shipping_address_line2?: string | null;
  shipping_postal_code?: string | null;
  shipping_city?: string | null;
  shipping_country?: string | null;
  shipping_phone?: string | null;
}

interface AdminReview {
  id: number;
  order_id: number | null;
  product_id: number | null;
  rating: number;
  comment: string;
  author_name: string | null;
  created_at: string;
  product_name: string;
  category: string | null;
  image: string | null;
}

type FormMode = 'create' | 'edit';

const Admin: React.FC = () => {
  const {
    products,
    addProduct,
    updateProduct,
    deleteProduct,
    resetProducts,
    settings,
    setLowStockThreshold,
  } = useProducts();

  const { token, user } = useAuth();

  const totalProducts = products.length;
  const categories = Array.from(
    new Set(products.map((p) => p.category).filter((c) => !!c))
  );
  const averagePrice =
    products.reduce((sum, p) => sum + p.price, 0) / (products.length || 1);

  const [mode, setMode] = useState<FormMode>('create');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState({
    name: '',
    price: '',
    category: '',
    image: '',
    extraImages: '',
    colors: '',
    sizes: '',
    stock: '',
    isNew: false,
    discount: '',
    description: '',
    composition: '',
  });
  const [sizeStocks, setSizeStocks] = useState<Record<string, string>>({});
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [sizeStockData, setSizeStockData] = useState<Record<number, SizeStockMap>>({});

  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState<string | null>(null);

  const [miniOrders, setMiniOrders] = useState<MiniOrderRecord[]>([]);
  const [miniOrdersLoading, setMiniOrdersLoading] = useState(false);
  const [miniOrdersError, setMiniOrdersError] = useState<string | null>(null);

  const [reviews, setReviews] = useState<AdminReview[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState<string | null>(null);

  const [promoCodes, setPromoCodes] = useState<any[]>([]);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [promoForm, setPromoForm] = useState({
    code: '',
    discountPercent: '',
    expiresAt: '',
    noEndDate: true,
  });

  const [isPinValidated, setIsPinValidated] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<
    'all' | 'new' | 'in_progress' | 'completed' | 'new_and_in_progress'
  >('all');

  const [reviewProductFilter, setReviewProductFilter] = useState<'all' | number>('all');
  const [reviewCategoryFilter, setReviewCategoryFilter] = useState<'all' | string>('all');
  const [reviewOrderFilter, setReviewOrderFilter] = useState<'all' | number>('all');

  const resetForm = () => {
    setMode('create');
    setEditingId(null);
    setFormValues({
      name: '',
      price: '',
      category: '',
      image: '',
      extraImages: '',
      colors: '',
      sizes: '',
      stock: '',
      isNew: false,
      discount: '',
      description: '',
      composition: '',
    });
    setSizeStocks({});
    setUploadedImages([]);
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const startEdit = (product: Product) => {
    setMode('edit');
    setEditingId(product.id);

    const allImages =
      product.images && product.images.length > 0
        ? product.images
        : product.image
        ? [product.image]
        : [];
    const primaryImage = product.image || allImages[0] || '';
    const otherImages = allImages.filter((img) => img !== primaryImage);
    const urlExtras = otherImages.filter((img) => img.startsWith('http'));
    const uploadedExtras = otherImages.filter((img) => !img.startsWith('http'));

    setFormValues({
      name: product.name,
      price: String(product.price),
      category: product.category,
      image: primaryImage,
      extraImages: urlExtras.join(', '),
      colors: product.colors.join(', '),
      sizes: (product.sizes || []).join(', '),
      stock:
        product.stock != null
          ? String(product.stock)
          : product.inStock
          ? '1'
          : '0',
      isNew: !!product.isNew,
      discount: product.discount != null ? String(product.discount) : '',
      description: product.description || '',
      composition: product.composition || '',
    });
    setUploadedImages(uploadedExtras);

    const sizeStockFromProduct: Record<string, string> = {};
    const productSizes = product.sizes || [];
    const backendSizeStock = sizeStockData[product.id] || {};

    if (productSizes.length > 0) {
      productSizes.forEach((size) => {
        const key = String(size);
        const qty = backendSizeStock[key];
        if (typeof qty === 'number' && Number.isFinite(qty) && qty >= 0) {
          sizeStockFromProduct[key] = String(qty);
        } else {
          sizeStockFromProduct[key] = '';
        }
      });
    }

    setSizeStocks(sizeStockFromProduct);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const price = parseFloat(formValues.price.replace(',', '.')) || 0;
    const discount =
      formValues.discount.trim() !== ''
        ? parseFloat(formValues.discount.replace(',', '.')) || 0
        : undefined;

    const colors = formValues.colors
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);
    const sizes = formValues.sizes
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const sizeStockMap: SizeStockMap = {};
    sizes.forEach((size) => {
      const key = size;
      const raw = sizeStocks[key];
      if (raw == null || raw.trim() === '') {
        return;
      }
      const parsed = parseInt(raw, 10);
      if (!Number.isFinite(parsed) || parsed < 0) {
        return;
      }
      sizeStockMap[key] = parsed;
    });

    const hasSizeStock = Object.keys(sizeStockMap).length > 0;
    const totalFromSizes = hasSizeStock
      ? Object.values(sizeStockMap).reduce((sum, qty) => sum + qty, 0)
      : 0;

    const stockValue = parseInt(formValues.stock, 10);
    const safeStock = hasSizeStock
      ? totalFromSizes
      : Number.isFinite(stockValue) && stockValue > 0
      ? stockValue
      : 0;

    const extraImageUrls = formValues.extraImages
      .split(',')
      .map((url) => url.trim())
      .filter(Boolean);

    let allImages: string[] = [];

    if (formValues.image.trim()) {
      allImages.push(formValues.image.trim());
    }
    allImages = [...allImages, ...uploadedImages, ...extraImageUrls];

    const uniqueImages = Array.from(new Set(allImages));
    const mainImage = uniqueImages[0] || formValues.image.trim();

    if (!mainImage) {
      window.alert(
        'Un produit doit avoir au moins une image. Ajoutez une image principale avant de sauvegarder.'
      );
      return;
    }

    const baseData: Omit<Product, 'id'> = {
      name: formValues.name,
      price,
      category: formValues.category || 'autre',
      image: mainImage,
      images: uniqueImages.length > 0 ? uniqueImages : undefined,
      colors: colors.length ? colors : ['#000000'],
      sizes: sizes.length ? sizes : undefined,
      stock: safeStock,
      inStock: safeStock > 0,
      isNew: formValues.isNew,
      discount,
      description: formValues.description || undefined,
      composition: formValues.composition || undefined,
      rating: 0,
      reviews: 0,
      isFavorite: false,
    };

    if (mode === 'create') {
      if (hasSizeStock) {
        addProduct(baseData, {
          onCreated: (created) => {
            void saveSizeStockForProduct(created.id, sizeStockMap);
            setSizeStockData((prev) => ({
              ...prev,
              [created.id]: sizeStockMap,
            }));
          },
        });
      } else {
        addProduct(baseData);
      }
    } else if (mode === 'edit' && editingId != null) {
      updateProduct(editingId, baseData);
      if (hasSizeStock) {
        void saveSizeStockForProduct(editingId, sizeStockMap);
        setSizeStockData((prev) => ({
          ...prev,
          [editingId]: sizeStockMap,
        }));
      }
    }

    resetForm();
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Supprimer ce produit ?')) {
      deleteProduct(id);
      if (editingId === id) {
        resetForm();
      }
    }
  };

  const sendReviewEmail = async (orderId: number) => {
    if (!token) return;

    const confirmSend = window.confirm(
      "Envoyer un e-mail au client pour lui demander un avis sur sa commande ?"
    );
    if (!confirmSend) {
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE_URL}/admin/orders/${orderId}/send-review-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        const message =
          data && data.error
            ? data.error
            : "Erreur lors de l'envoi de l'e-mail d'avis.";
        window.alert(message);
        return;
      }

      setOrders((prev) =>
        prev.map((o) =>
          o.id === orderId ? { ...o, review_email_sent: true } : o
        )
      );

      window.alert("E-mail d'avis envoyé au client.");
    } catch (e) {
      console.error("Erreur réseau lors de l'envoi de l'e-mail d'avis", e);
      window.alert("Erreur réseau lors de l'envoi de l'e-mail d'avis.");
    }
  };

  const handleResetProducts = () => {
    if (window.confirm('Réinitialiser la liste des produits par défaut ?')) {
      resetProducts();
      resetForm();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    if (!token) return;

    const fileArray = Array.from(files);

    Promise.all(
      fileArray.map(
        (file) =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const originalDataUrl = reader.result as string;

              compressImageDataUrl(originalDataUrl, 0.8)
                .then(async (dataUrl) => {
                  try {
                    const res = await fetch(`${API_BASE_URL}/admin/upload-image`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                      },
                      body: JSON.stringify({ dataUrl }),
                    });

                    if (!res.ok) {
                      const data = await res.json().catch(() => null);
                      console.error(
                        "Erreur lors de l'upload de l'image vers l'hébergement statique",
                        data && data.error ? data.error : res.statusText
                      );
                      reject(new Error('Upload échoué'));
                      return;
                    }

                    const data = await res.json();
                    if (!data || typeof data.url !== 'string') {
                      reject(new Error('Réponse invalide du serveur pour upload image'));
                      return;
                    }

                    resolve(data.url as string);
                  } catch (e) {
                    console.error("Erreur lors de la lecture ou de l'upload de l'image", e);
                    reject(e);
                  }
                })
                .catch((e) => {
                  reject(e);
                });
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
          })
      )
    )
      .then((urls) => {
        setUploadedImages((prev) => [...prev, ...urls]);
      })
      .catch((error) => {
        console.error("Erreur lors du chargement ou de l'upload des images", error);
      });
  };

  const handleRemoveImage = (src: string) => {
    setUploadedImages((prev) => prev.filter((img) => img !== src));

    setFormValues((prev) => {
      const trimmedMain = prev.image.trim();
      const urlExtras = prev.extraImages
        .split(',')
        .map((u) => u.trim())
        .filter(Boolean)
        .filter((u) => u !== src);

      const newMain = trimmedMain === src ? '' : trimmedMain;

      return {
        ...prev,
        image: newMain,
        extraImages: urlExtras.join(', '),
      };
    });
  };

  const openPreview = (src: string) => {
    setPreviewImage(src);
    setIsPreviewOpen(true);
  };

  const closePreview = () => {
    setIsPreviewOpen(false);
    setPreviewImage(null);
  };

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('admin_pin_valid');
      if (stored === '1') {
        setIsPinValidated(true);
      }
    } catch (e) {
      
    }
  }, []);

  const handlePinSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const normalized = pinInput.replace(/\s+/g, '');
    if (normalized === ADMIN_PIN) {
      setIsPinValidated(true);
      setPinError(null);
      try {
        sessionStorage.setItem('admin_pin_valid', '1');
      } catch (e) {
        
      }
    } else {
      setPinError('Code incorrect.');
    }
  };

  const isAdminUser =
    !!user &&
    typeof user.email === 'string' &&
    user.email.toLowerCase() === ADMIN_EMAIL.toLowerCase();

  useEffect(() => {
    const loadOrders = async () => {
      if (!token || !isAdminUser) {
        setOrders([]);
        return;
      }

      setOrdersLoading(true);
      setOrdersError(null);
      try {
        const res = await fetch(`${API_BASE_URL}/admin/orders`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          setOrdersError('Erreur lors du chargement des commandes.');
          setOrders([]);
          return;
        }

        const data = await res.json();
        if (Array.isArray(data)) {
          const normalized: AdminOrder[] = data.map((o: any) => ({
            id: o.id,
            created_at: o.created_at,
            total_amount: typeof o.total_amount === 'number' ? o.total_amount : Number(o.total_amount) || 0,
            status: (o.status as AdminOrderStatus) || 'new',
            user_email: o.user_email,
            user_name: o.user_name ?? null,
            review_email_sent: o.review_email_sent ?? false,
            shipping_name: o.shipping_name ?? null,
            shipping_address_line1: o.shipping_address_line1 ?? null,
            shipping_address_line2: o.shipping_address_line2 ?? null,
            shipping_postal_code: o.shipping_postal_code ?? null,
            shipping_city: o.shipping_city ?? null,
            shipping_country: o.shipping_country ?? null,
            shipping_phone: o.shipping_phone ?? null,
          }));
          setOrders(normalized);
        } else {
          setOrders([]);
        }
      } catch (e) {
        console.error('Erreur lors du chargement des commandes admin', e);
        setOrdersError('Erreur réseau lors du chargement des commandes.');
        setOrders([]);
      } finally {
        setOrdersLoading(false);
      }
    };

    const loadMiniOrders = async () => {
      if (!isAdminUser) {
        setMiniOrders([]);
        return;
      }

      setMiniOrdersLoading(true);
      setMiniOrdersError(null);
      try {
        const data = await fetchMiniOrders();
        if (Array.isArray(data)) {
          setMiniOrders(data);
        } else {
          setMiniOrders([]);
        }
      } catch (e) {
        console.error('Erreur lors du chargement des commandes (mini backend)', e);
        setMiniOrdersError('Erreur lors du chargement du détail des commandes (mini backend).');
        setMiniOrders([]);
      } finally {
        setMiniOrdersLoading(false);
      }
    };

    const loadPromoCodes = async () => {
      if (!token || !isAdminUser) {
        setPromoCodes([]);
        return;
      }

      setPromoLoading(true);
      setPromoError(null);
      try {
        const res = await fetch(`${API_BASE_URL}/admin/promo-codes`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          setPromoError('Erreur lors du chargement des codes promo.');
          setPromoCodes([]);
          return;
        }

        const data = await res.json();
        if (Array.isArray(data)) {
          setPromoCodes(data);
        } else {
          setPromoCodes([]);
        }
      } catch (e) {
        console.error('Erreur lors du chargement des codes promo', e);
        setPromoError('Erreur réseau lors du chargement des codes promo.');
        setPromoCodes([]);
      } finally {
        setPromoLoading(false);
      }
    };

    const loadReviews = async () => {
      if (!token || !isAdminUser) {
        setReviews([]);
        return;
      }

      setReviewsLoading(true);
      setReviewsError(null);
      try {
        const res = await fetch(`${API_BASE_URL}/admin/reviews`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          setReviewsError('Erreur lors du chargement des avis.');
          setReviews([]);
          return;
        }

        const data = await res.json();
        if (Array.isArray(data)) {
          const normalized: AdminReview[] = data.map((r: any) => ({
            id: r.id,
            order_id: r.order_id ?? null,
            product_id: r.product_id ?? null,
            rating:
              typeof r.rating === 'number'
                ? r.rating
                : Number(r.rating) || 0,
            comment:
              typeof r.comment === 'string'
                ? r.comment
                : '',
            author_name: r.author_name ?? null,
            created_at: r.created_at,
            product_name:
              typeof r.product_name === 'string' && r.product_name.trim()
                ? r.product_name.trim()
                : 'Produit inconnu',
            category: r.category ?? null,
            image: r.image ?? null,
          }));
          setReviews(normalized);
        } else {
          setReviews([]);
        }
      } catch (e) {
        console.error('Erreur lors du chargement des avis admin', e);
        setReviewsError('Erreur réseau lors du chargement des avis.');
        setReviews([]);
      } finally {
        setReviewsLoading(false);
      }
    };

    const refreshAll = () => {
      void loadOrders();
      void loadMiniOrders();
      void loadPromoCodes();
      void loadReviews();
    };

    refreshAll();

    const handleFocus = () => {
      refreshAll();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [token, isAdminUser]);

  const updateOrderStatus = async (orderId: number, newStatus: AdminOrderStatus) => {
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE_URL}/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!res.ok) {
        console.error('Erreur lors de la mise à jour du statut de commande');
        return;
      }

      const updated = await res.json();
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status: updated.status as AdminOrderStatus } : o))
      );
    } catch (e) {
      console.error('Erreur réseau lors de la mise à jour du statut de commande', e);
    }
  };

  const handlePromoInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setPromoForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePromoNoEndDateChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = event.target.checked;
    setPromoForm((prev) => ({
      ...prev,
      noEndDate: checked,
      expiresAt: checked ? '' : prev.expiresAt,
    }));
  };

  const handleSubmitPromo = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!token) return;

    setPromoError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/promo-codes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          code: promoForm.code,
          discountPercent: promoForm.discountPercent,
          expiresAt: promoForm.noEndDate ? null : promoForm.expiresAt,
          noEndDate: promoForm.noEndDate,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        const message = data && data.error ? data.error : "Erreur lors de l'enregistrement du code promo.";
        setPromoError(message);
        return;
      }

      const created = await res.json();
      setPromoCodes((prev) => {
        const without = prev.filter((p: any) => p.id !== created.id);
        return [created, ...without];
      });

      setPromoForm({
        code: '',
        discountPercent: '',
        expiresAt: '',
        noEndDate: true,
      });
    } catch (e) {
      console.error('Erreur réseau lors de la création du code promo', e);
      setPromoError('Erreur réseau lors de la création du code promo.');
    }
  };

  const handleDeletePromo = async (id: number) => {
    if (!token) return;
    if (!window.confirm('Désactiver ce code promo ?')) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/admin/promo-codes/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        console.error('Erreur lors de la désactivation du code promo');
        return;
      }

      setPromoCodes((prev) => prev.filter((p: any) => p.id !== id));
    } catch (e) {
      console.error('Erreur réseau lors de la désactivation du code promo', e);
    }
  };

  const handleDeleteReview = async (id: number) => {
    if (!token) return;
    if (!window.confirm("Supprimer cet avis ?")) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/admin/reviews/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        window.alert("Erreur lors de la suppression de l'avis.");
        return;
      }

      setReviews((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      console.error("Erreur réseau lors de la suppression de l'avis", e);
      window.alert("Erreur réseau lors de la suppression de l'avis.");
    }
  };

  const productFilterOptions = Array.from(
    new Map(
      reviews
        .filter((r) => r.product_id != null)
        .map((r) => [r.product_id as number, r.product_name])
    ).entries()
  )
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const categoryFilterOptions = Array.from(
    new Set(
      reviews
        .map((r) => r.category)
        .filter((c): c is string => !!c && c.trim() !== '')
    )
  ).sort((a, b) => a.localeCompare(b));

  const orderFilterOptions = orders
    .filter((o) => o.review_email_sent)
    .map((o) => o.id)
    .sort((a, b) => a - b);

  const filteredReviews = reviews.filter((review) => {
    if (reviewProductFilter !== 'all' && review.product_id !== reviewProductFilter) {
      return false;
    }

    if (reviewCategoryFilter !== 'all' && review.category !== reviewCategoryFilter) {
      return false;
    }

    if (reviewOrderFilter !== 'all' && review.order_id !== reviewOrderFilter) {
      return false;
    }

    return true;
  });

  const filteredOrders = orders.filter((order) => {
    const status = (order.status as AdminOrderStatus) || 'new';
    switch (statusFilter) {
      case 'new':
        return status === 'new';
      case 'in_progress':
        return status === 'in_progress';
      case 'completed':
        return status === 'completed';
      case 'new_and_in_progress':
        return status === 'new' || status === 'in_progress';
      case 'all':
      default:
        return true;
    }
  });

  if (!isAdminUser) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pt: 10 }}>
        <Container maxWidth="sm" sx={{ py: 6 }}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="h5" component="h1" gutterBottom>
              Accès refusé
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cette page est réservée à l'administrateur du site.
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (!isPinValidated) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pt: 10 }}>
        <Container maxWidth="sm" sx={{ py: 6 }}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="h5" component="h1" gutterBottom>
              Accès administrateur
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Saisissez le code à 6 chiffres pour accéder à l'interface d'administration.
            </Typography>
            <Box component="form" onSubmit={handlePinSubmit} noValidate sx={{ mt: 2 }}>
              <TextField
                label="Code administrateur"
                value={pinInput}
                onChange={(event) => setPinInput(event.target.value)}
                fullWidth
                inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                sx={{ mb: 2 }}
              />
              {pinError && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                  {pinError}
                </Typography>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button type="submit" variant="contained" color="primary">
                  Valider
                </Button>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pt: 10 }}>
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Administration des produits
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Interface d'administration pour gérer les produits Bratan Family (stock, prix,
          catégories, visuels, etc.).
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 3,
            mb: 4,
          }}
        >
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Nombre de produits
            </Typography>
            <Typography variant="h5">{totalProducts}</Typography>
          </Paper>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Catégories
            </Typography>
            <Typography variant="h5">{categories.length}</Typography>
          </Paper>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Prix moyen
            </Typography>
            <Typography variant="h5">{averagePrice.toFixed(2)} €</Typography>
          </Paper>
        </Box>

        <Paper sx={{ p: 2, mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom>
            Paramètres produits
          </Typography>
          <TextField
            label="Seuil de stock faible (global)"
            type="number"
            value={settings.lowStockThreshold}
            onChange={(event) => {
              const value = parseInt(event.target.value, 10);
              const safe = Number.isFinite(value) && value >= 0 ? value : 0;
              setLowStockThreshold(safe);
            }}
            inputProps={{ min: 0, step: '1' }}
            sx={{ maxWidth: 260 }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.5 }}
          >
            En dessous ou égal à ce seuil, les produits seront marqués comme "Stock faible".
          </Typography>
        </Paper>

        <Paper sx={{ p: 3, mb: 4 }}>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h6">
                {mode === 'create' ? 'Ajouter un produit' : 'Modifier le produit'}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  type="button"
                  variant="outlined"
                  color="inherit"
                  onClick={resetForm}
                >
                  Nouveau
                </Button>
                <Button
                  type="button"
                  variant="text"
                  color="secondary"
                  startIcon={<RestoreIcon />}
                  onClick={handleResetProducts}
                >
                  Réinitialiser les produits
                </Button>
              </Stack>
            </Box>

            <Stack spacing={2}>
              <TextField
                label="Nom"
                name="name"
                value={formValues.name}
                onChange={handleChange}
                fullWidth
                required
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Prix (€)"
                  name="price"
                  type="number"
                  value={formValues.price}
                  onChange={handleChange}
                  fullWidth
                  required
                  inputProps={{ step: '0.01' }}
                />
                <Autocomplete
                  freeSolo
                  options={categories}
                  value={formValues.category}
                  onChange={(_event, newValue) => {
                    setFormValues((prev) => ({
                      ...prev,
                      category: (newValue as string) || '',
                    }));
                  }}
                  onInputChange={(_event, newInputValue) => {
                    setFormValues((prev) => ({
                      ...prev,
                      category: newInputValue,
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Catégorie"
                      fullWidth
                    />
                  )}
                />
              </Stack>
              <TextField
                label="URL de l'image"
                name="image"
                value={formValues.image}
                onChange={handleChange}
                fullWidth
              />
              <Box>
                <input
                  id="product-images-upload"
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
                <Button
                  variant="outlined"
                  color="inherit"
                  component="label"
                  htmlFor="product-images-upload"
                  sx={{ mt: 1 }}
                >
                  Ajouter des images depuis mon appareil
                </Button>
                {uploadedImages.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {uploadedImages.length} image(s) sélectionnée(s)
                  </Typography>
                )}
              </Box>
              <TextField
                label="Images supplémentaires (URLs séparées par des virgules)"
                name="extraImages"
                value={formValues.extraImages}
                onChange={handleChange}
                fullWidth
              />
              {(() => {
                const urlExtras = formValues.extraImages
                  .split(',')
                  .map((u) => u.trim())
                  .filter(Boolean);
                const previewImages = Array.from(
                  new Set([
                    ...(formValues.image.trim() ? [formValues.image.trim()] : []),
                    ...uploadedImages,
                    ...urlExtras,
                  ])
                );

                if (previewImages.length === 0) {
                  return null;
                }

                return (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Aperçu des images
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {previewImages.map((src) => (
                        <Box key={src} sx={{ position: 'relative' }}>
                          <Box
                            component="img"
                            src={src}
                            alt="Aperçu produit"
                            sx={{
                              width: 64,
                              height: 64,
                              objectFit: 'cover',
                              borderRadius: 1,
                              cursor: 'pointer',
                              border: '1px solid rgba(0,0,0,0.12)',
                            }}
                            onClick={() => openPreview(src)}
                          />
                          <IconButton
                            size="small"
                            color="error"
                            sx={{
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              bgcolor: 'background.paper',
                              '&:hover': {
                                bgcolor: 'background.paper',
                              },
                            }}
                            onClick={() => handleRemoveImage(src)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })()}
              <TextField
                label="Couleurs (codes hex séparés par des virgules)"
                name="colors"
                value={formValues.colors}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                label="Tailles (séparées par des virgules)"
                name="sizes"
                value={formValues.sizes}
                onChange={handleChange}
                fullWidth
              />
              {(() => {
                const sizes = formValues.sizes
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean);

                if (sizes.length === 0) {
                  return null;
                }

                return (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                      gap: 1,
                    }}
                  >
                    {sizes.map((size) => (
                      <TextField
                        key={size}
                        label={`Stock taille ${size}`}
                        type="number"
                        value={sizeStocks[size] ?? ''}
                        onChange={(event) =>
                          setSizeStocks((prev) => ({
                            ...prev,
                            [size]: event.target.value,
                          }))
                        }
                        inputProps={{ min: 0, step: '1' }}
                        fullWidth
                      />
                    ))}
                  </Box>
                );
              })()}
              <TextField
                label="Quantité en stock"
                name="stock"
                type="number"
                value={formValues.stock}
                onChange={handleChange}
                fullWidth
                inputProps={{ min: 0, step: '1' }}
              />
              <TextField
                label="Remise (%)"
                name="discount"
                type="number"
                value={formValues.discount}
                onChange={handleChange}
                fullWidth
                inputProps={{ min: 0, max: 100, step: '1' }}
              />
              <TextField
                label="Description"
                name="description"
                value={formValues.description}
                onChange={handleChange}
                fullWidth
                multiline
                minRows={3}
              />
              <TextField
                label="Composition"
                name="composition"
                value={formValues.composition}
                onChange={handleChange}
                fullWidth
                multiline
                minRows={2}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formValues.isNew}
                      onChange={(event) =>
                        setFormValues((prev) => ({
                          ...prev,
                          isNew: event.target.checked,
                        }))
                      }
                      color="primary"
                    />
                  }
                  label="Nouveau produit"
                />
              </Stack>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button type="submit" variant="contained" color="primary">
                  {mode === 'create' ? 'Ajouter le produit' : 'Enregistrer les modifications'}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Produits
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
              gap: 1,
              mb: 1,
              alignItems: 'center',
            }}
          >
            <Typography variant="subtitle2">Nom</Typography>
            <Typography variant="subtitle2">Catégorie</Typography>
            <Typography variant="subtitle2">Prix</Typography>
            <Typography variant="subtitle2">Stock</Typography>
            <Typography variant="subtitle2">Actions</Typography>
          </Box>
          {products.map((product) => (
            <Box
              key={product.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
                gap: 1,
                py: 0.5,
                borderTop: '1px solid rgba(0,0,0,0.06)',
                alignItems: 'center',
              }}
            >
              <Typography variant="body2">{product.name}</Typography>
              <Typography variant="body2">{product.category}</Typography>
              <Typography variant="body2">{product.price.toFixed(2)} €</Typography>
              <Typography variant="body2">
                {product.stock != null ? `${product.stock} en stock` : product.inStock ? 'En stock' : 'Rupture de stock'}
              </Typography>
              <Box>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => startEdit(product)}
                  aria-label="Modifier le produit"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDelete(product.id)}
                  aria-label="Supprimer le produit"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Paper>

        <Paper sx={{ p: 2, mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Codes promo
          </Typography>
          <Box component="form" onSubmit={handleSubmitPromo} noValidate sx={{ mb: 3 }}>
            <Stack
              spacing={2}
              direction={{ xs: 'column', sm: 'row' }}
              sx={{ alignItems: { xs: 'stretch', sm: 'flex-end' } }}
            >
              <TextField
                label="Code"
                name="code"
                value={promoForm.code}
                onChange={(event) => {
                  const value = event.target.value.toUpperCase();
                  setPromoForm((prev) => ({ ...prev, code: value }));
                }}
                fullWidth
                required
              />
              <TextField
                label="Réduction (%)"
                name="discountPercent"
                type="number"
                value={promoForm.discountPercent}
                onChange={handlePromoInputChange}
                fullWidth
                required
                inputProps={{ min: 1, max: 100, step: '1' }}
              />
              <TextField
                label="Date de fin"
                name="expiresAt"
                type="date"
                value={promoForm.expiresAt}
                onChange={handlePromoInputChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                disabled={promoForm.noEndDate}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={promoForm.noEndDate}
                    onChange={handlePromoNoEndDateChange}
                    color="primary"
                  />
                }
                label="Pas de date de fin"
              />
              <Box sx={{ flexGrow: 1 }} />
              <Button type="submit" variant="contained" color="primary">
                Enregistrer le code
              </Button>
            </Stack>
            {promoError && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {promoError}
              </Typography>
            )}
          </Box>

          {promoLoading ? (
            <Typography variant="body2" color="text.secondary">
              Chargement des codes promo...
            </Typography>
          ) : promoCodes.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Aucun code promo pour le moment.
            </Typography>
          ) : (
            <Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr 1fr',
                  gap: 1,
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2">Code</Typography>
                <Typography variant="subtitle2">Réduction</Typography>
                <Typography variant="subtitle2">Expiration</Typography>
                <Typography variant="subtitle2">Actions</Typography>
              </Box>
              {promoCodes.map((promo) => (
                <Box
                  key={promo.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr 1fr 1fr',
                    gap: 1,
                    py: 0.5,
                    borderTop: '1px solid rgba(0,0,0,0.06)',
                    alignItems: 'center',
                    opacity: promo.is_active === false ? 0.5 : 1,
                  }}
                >
                  <Typography variant="body2">{promo.code}</Typography>
                  <Typography variant="body2">
                    {Number(promo.discount_percent).toFixed(0)}%
                  </Typography>
                  <Typography variant="body2">
                    {promo.expires_at
                      ? new Date(promo.expires_at).toLocaleDateString()
                      : 'Pas de fin'}
                  </Typography>
                  <Box>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeletePromo(promo.id)}
                      aria-label="Désactiver le code"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Paper>

        <Paper sx={{ p: 2, mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Commandes
          </Typography>
          {!token && (
            <Typography variant="body2" color="text.secondary">
              Vous devez être connecté pour voir les commandes.
            </Typography>
          )}
          {token && (
            <>
              {ordersError && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                  {ordersError}
                </Typography>
              )}
              {miniOrdersError && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                  {miniOrdersError}
                </Typography>
              )}
              {ordersLoading ? (
                <Typography variant="body2" color="text.secondary">
                  Chargement des commandes...
                </Typography>
              ) : orders.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Aucune commande pour le moment.
                </Typography>
              ) : (
                <>
                  <Box
                    sx={{
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography variant="body2">Filtrer par statut :</Typography>
                    <Select
                      size="small"
                      value={statusFilter}
                      onChange={(event) =>
                        setStatusFilter(
                          event.target.value as
                            | 'all'
                            | 'new'
                            | 'in_progress'
                            | 'completed'
                            | 'new_and_in_progress'
                        )
                      }
                    >
                      <MenuItem value="all">Toutes</MenuItem>
                      <MenuItem value="new">Nouvelles</MenuItem>
                      <MenuItem value="in_progress">En cours</MenuItem>
                      <MenuItem value="completed">Terminées</MenuItem>
                      <MenuItem value="new_and_in_progress">Nouvelles + En cours</MenuItem>
                    </Select>
                  </Box>
                  <Box sx={{ width: '100%', overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 800 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Commande</TableCell>
                          <TableCell>Client</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Montant</TableCell>
                          <TableCell>Articles</TableCell>
                          <TableCell>Adresse de livraison</TableCell>
                          <TableCell>Statut</TableCell>
                          <TableCell>Avis</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredOrders.map((order) => (
                          <TableRow key={order.id}>
                            <TableCell>#{order.id}</TableCell>
                            <TableCell>
                              <Typography variant="body2">{order.user_email}</Typography>
                              {order.user_name && (
                                <Typography variant="caption" color="text.secondary">
                                  {order.user_name}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {new Date(order.created_at).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              {Number(order.total_amount).toFixed(2)} €
                            </TableCell>
                            <TableCell>
                              {miniOrdersLoading ? (
                                <Typography variant="body2" color="text.secondary">
                                  Chargement du détail...
                                </Typography>
                              ) : (() => {
                                const miniOrder = miniOrders.find(
                                  (mo) => mo.orderId === order.id
                                );

                                if (!miniOrder || !miniOrder.items || miniOrder.items.length === 0) {
                                  return (
                                    <Typography variant="body2" color="text.secondary">
                                      Détail non disponible
                                    </Typography>
                                  );
                                }

                                return (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {miniOrder.items.map((item, index) => {
                                      const colorLabel = item.color
                                        ? getColorLabel(item.color)
                                        : undefined;

                                      const cleanedName =
                                        colorLabel && typeof item.name === 'string'
                                          ? item.name.replace(/#[0-9a-fA-F]{3,6}/g, colorLabel)
                                          : item.name;

                                      return (
                                        <Typography key={index} variant="body2">
                                          {item.quantity}x {cleanedName}
                                          {item.size ? ` - Taille ${item.size}` : ''}
                                          {colorLabel ? ` - Couleur ${colorLabel}` : ''}
                                        </Typography>
                                      );
                                    })}
                                  </Box>
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              {order.shipping_name && (
                                <Typography variant="body2">{order.shipping_name}</Typography>
                              )}
                              {(order.shipping_address_line1 || order.shipping_address_line2) && (
                                <Typography variant="body2">
                                  {[order.shipping_address_line1, order.shipping_address_line2]
                                    .filter(Boolean)
                                    .join(' ')}
                                </Typography>
                              )}
                              {(order.shipping_postal_code || order.shipping_city || order.shipping_country) && (
                                <Typography variant="body2">
                                  {[order.shipping_postal_code, order.shipping_city, order.shipping_country]
                                    .filter(Boolean)
                                    .join(' ')}
                                </Typography>
                              )}
                              {order.shipping_phone && (
                                <Typography variant="caption" color="text.secondary">
                                  Tél. : {order.shipping_phone}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Select
                                value={order.status as AdminOrderStatus}
                                size="small"
                                onChange={(event) =>
                                  updateOrderStatus(order.id, event.target.value as AdminOrderStatus)
                                }
                              >
                                <MenuItem value="new">Nouveau</MenuItem>
                                <MenuItem value="in_progress">En cours</MenuItem>
                                <MenuItem value="completed">Terminé</MenuItem>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => sendReviewEmail(order.id)}
                                disabled={!!order.review_email_sent}
                              >
                                {order.review_email_sent
                                  ? 'Demande envoyée'
                                  : 'Demander un avis'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </>
              )}
            </>
          )}
        </Paper>

        <Paper sx={{ p: 2, mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Avis clients
          </Typography>
          {!token && (
            <Typography variant="body2" color="text.secondary">
              Vous devez être connecté pour voir les avis.
            </Typography>
          )}
          {token && (
            <>
              {reviewsError && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                  {reviewsError}
                </Typography>
              )}
              {reviewsLoading ? (
                <Typography variant="body2" color="text.secondary">
                  Chargement des avis...
                </Typography>
              ) : reviews.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Aucun avis pour le moment.
                </Typography>
              ) : (
                <>
                  <Box
                    sx={{
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography variant="body2">Filtrer par produit :</Typography>
                    <Select
                      size="small"
                      value={
                        reviewProductFilter === 'all'
                          ? 'all'
                          : String(reviewProductFilter)
                      }
                      onChange={(event) => {
                        const value = event.target.value;
                        if (value === 'all') {
                          setReviewProductFilter('all');
                        } else {
                          const num = Number(value);
                          setReviewProductFilter(
                            Number.isFinite(num) ? num : 'all'
                          );
                        }
                      }}
                    >
                      <MenuItem value="all">Tous les produits</MenuItem>
                      {productFilterOptions.map((opt) => (
                        <MenuItem key={opt.id} value={String(opt.id)}>
                          {opt.name}
                        </MenuItem>
                      ))}
                    </Select>

                    <Typography variant="body2">Filtrer par catégorie :</Typography>
                    <Select
                      size="small"
                      value={reviewCategoryFilter === 'all' ? 'all' : reviewCategoryFilter}
                      onChange={(event) => {
                        const value = event.target.value;
                        if (value === 'all') {
                          setReviewCategoryFilter('all');
                        } else {
                          setReviewCategoryFilter(String(value));
                        }
                      }}
                    >
                      <MenuItem value="all">Toutes les catégories</MenuItem>
                      {categoryFilterOptions.map((cat) => (
                        <MenuItem key={cat} value={cat}>
                          {cat}
                        </MenuItem>
                      ))}
                    </Select>

                    <Typography variant="body2">
                      Filtrer par n° de commande (mail d'avis envoyé) :
                    </Typography>
                    <Select
                      size="small"
                      value={
                        reviewOrderFilter === 'all'
                          ? 'all'
                          : String(reviewOrderFilter)
                      }
                      onChange={(event) => {
                        const value = event.target.value;
                        if (value === 'all') {
                          setReviewOrderFilter('all');
                        } else {
                          const num = Number(value);
                          setReviewOrderFilter(
                            Number.isFinite(num) ? num : 'all'
                          );
                        }
                      }}
                    >
                      <MenuItem value="all">Toutes les commandes</MenuItem>
                      {orderFilterOptions.map((orderId) => (
                        <MenuItem key={orderId} value={String(orderId)}>
                          #{orderId}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>

                  <Box sx={{ width: '100%', overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 900 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Produit</TableCell>
                          <TableCell>Catégorie</TableCell>
                          <TableCell>Note</TableCell>
                          <TableCell>Auteur</TableCell>
                          <TableCell>Commande</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Commentaire</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredReviews.map((review) => (
                          <TableRow key={review.id}>
                            <TableCell>{review.product_name}</TableCell>
                            <TableCell>{review.category || '-'}</TableCell>
                            <TableCell>{review.rating}/5</TableCell>
                            <TableCell>{review.author_name || 'Client'}</TableCell>
                            <TableCell>
                              {review.order_id ? `#${review.order_id}` : '-'}
                            </TableCell>
                            <TableCell>
                              {new Date(review.created_at).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ maxWidth: 320 }}
                              >
                                {review.comment || '(sans commentaire)'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="outlined"
                                size="small"
                                color="error"
                                onClick={() => handleDeleteReview(review.id)}
                              >
                                Supprimer
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </>
              )}
            </>
          )}
        </Paper>
        {isPreviewOpen && previewImage && (() => {
          const urlExtras = formValues.extraImages
            .split(',')
            .map((u) => u.trim())
            .filter(Boolean);
          const previewImages = Array.from(
            new Set([
              ...(formValues.image.trim() ? [formValues.image.trim()] : []),
              ...uploadedImages,
              ...urlExtras,
            ])
          );

          if (previewImages.length === 0) {
            return null;
          }

          const currentIndex = previewImages.findIndex((src) => src === previewImage);
          const safeIndex = currentIndex === -1 ? 0 : currentIndex;
          const currentSrc = previewImages[safeIndex];
          const hasPrev = safeIndex > 0;
          const hasNext = safeIndex < previewImages.length - 1;

          const handlePrev = (event: React.MouseEvent) => {
            event.stopPropagation();
            if (!hasPrev) return;
            const newIndex = safeIndex - 1;
            setPreviewImage(previewImages[newIndex]);
          };

          const handleNext = (event: React.MouseEvent) => {
            event.stopPropagation();
            if (!hasNext) return;
            const newIndex = safeIndex + 1;
            setPreviewImage(previewImages[newIndex]);
          };

          const handleDeleteCurrent = (event: React.MouseEvent) => {
            event.stopPropagation();
            handleRemoveImage(currentSrc);
            closePreview();
          };

          return (
            <Box
              sx={{
                position: 'fixed',
                inset: 0,
                bgcolor: 'rgba(0,0,0,0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1400,
              }}
              onClick={closePreview}
            >
              <IconButton
                onClick={handlePrev}
                disabled={!hasPrev}
                sx={{
                  position: 'absolute',
                  left: 24,
                  color: '#ffffff',
                  bgcolor: 'rgba(0,0,0,0.4)',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.6)',
                  },
                }}
              >
                <ChevronLeftIcon />
              </IconButton>

              <Box
                component="img"
                src={currentSrc}
                alt="Aperçu plein écran"
                sx={{
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                }}
                onClick={(event) => event.stopPropagation()}
              />

              <IconButton
                onClick={handleNext}
                disabled={!hasNext}
                sx={{
                  position: 'absolute',
                  right: 24,
                  color: '#ffffff',
                  bgcolor: 'rgba(0,0,0,0.4)',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.6)',
                  },
                }}
              >
                <ChevronRightIcon />
              </IconButton>

              <IconButton
                onClick={handleDeleteCurrent}
                sx={{
                  position: 'absolute',
                  top: 24,
                  right: 24,
                  color: '#ffffff',
                  bgcolor: 'rgba(0,0,0,0.4)',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.6)',
                  },
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          );
        })()}
      </Container>
    </Box>
  );
};

export default Admin;
